/**
 * Juni-2026 (User-Wunsch) — Gruppenbearbeitung (Bulk Edit) in der Disposition.
 *
 * Modal zum gleichzeitigen Aktualisieren mehrerer ausgewählter Fahrzeuge.
 * Jedes der 10 Felder hat eine eigene „ändern"-Checkbox — nur angehakte Felder
 * werden überschrieben (POST /api/dealer/dispo/bulk-update). Standort ist ein
 * Dropdown aus den Händler-Standorten.
 */
import { useState } from "react";
import { X, Loader2, Layers } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { COLOR_BASES } from "../../lib/listingConstants";

const FIELDS = [
    { key: "dispo_internal_note", label: "Interne Vermerke", type: "textarea" },
    { key: "model", label: "Hersteller-Modell", type: "text" },
    { key: "color", label: "Hersteller-Farbe", type: "text" },
    { key: "color_base", label: "Farbbasis", type: "select", options: COLOR_BASES },
    { key: "price", label: "Verkaufspreis (€)", type: "number" },
    { key: "title", label: "Titel", type: "text" },
    { key: "dispo_order_code", label: "Hersteller-Bestellcode", type: "text" },
    { key: "status", label: "Status", type: "select", options: ["verfügbar", "reserviert", "verkauft"] },
    { key: "site_code", label: "Standort", type: "site" },
    { key: "stand_platz", label: "Standplatz", type: "text" },
    { key: "key_depot", label: "Schlüsseldepot", type: "text" },
];

export default function DispoBulkEditModal({ count, listingIds, sites = [], onClose, onDone }) {
    const [enabled, setEnabled] = useState({});
    const [values, setValues] = useState({});
    const [saving, setSaving] = useState(false);

    const toggleField = (key) =>
        setEnabled((prev) => ({ ...prev, [key]: !prev[key] }));
    const setVal = (key, v) => setValues((prev) => ({ ...prev, [key]: v }));

    const activeKeys = FIELDS.filter((f) => enabled[f.key]).map((f) => f.key);

    const submit = async () => {
        const updates = {};
        for (const f of FIELDS) {
            if (!enabled[f.key]) continue;
            const v = values[f.key];
            // Auswahlfelder müssen einen Wert haben
            if ((f.type === "select" || f.type === "site") && !v) continue;
            updates[f.key] = v ?? "";
        }
        if (Object.keys(updates).length === 0) {
            toast.error("Bitte mindestens ein Feld anhaken und einen Wert eingeben.");
            return;
        }
        setSaving(true);
        try {
            const r = await api.post("/dealer/dispo/bulk-update", {
                listing_ids: listingIds,
                updates,
            });
            toast.success(`${r.data?.modified ?? 0} Fahrzeug(e) aktualisiert.`);
            onDone?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Aktualisierung fehlgeschlagen.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 bg-black/80 flex items-start justify-center p-4 overflow-y-auto"
            data-testid="dispo-bulk-edit-modal"
            onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
        >
            <div className="w-full max-w-2xl mt-10 bg-white border border-gray-200 rounded-sm shadow-xl">
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-[#111827] text-white rounded-t-sm">
                    <h2 className="font-display text-lg font-black flex items-center gap-2">
                        <Layers className="w-5 h-5 text-[#FFCC00]" />
                        Gruppenbearbeitung
                        <span className="text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm bg-[#FFCC00] text-[#111827]" data-testid="dispo-bulk-count">
                            {count} Fahrzeug{count === 1 ? "" : "e"}
                        </span>
                    </h2>
                    <button type="button" onClick={onClose} className="p-1 rounded-sm hover:bg-white/10" aria-label="Schließen" data-testid="dispo-bulk-close">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-5 space-y-3 max-h-[65vh] overflow-y-auto">
                    <p className="text-xs text-gray-500">
                        Haken Sie die Felder an, die für <strong>alle {count} ausgewählten Fahrzeuge</strong> überschrieben werden sollen. Nicht angehakte Felder bleiben unverändert.
                    </p>
                    {FIELDS.map((f) => {
                        const on = !!enabled[f.key];
                        return (
                            <div key={f.key} className={`flex items-start gap-3 p-2.5 rounded-sm border transition-colors ${on ? "border-[#FFCC00] bg-amber-50/40" : "border-gray-200"}`}>
                                <label className="flex items-center gap-2 shrink-0 w-44 pt-1.5 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={on}
                                        onChange={() => toggleField(f.key)}
                                        className="w-4 h-4 accent-[#FFCC00]"
                                        data-testid={`dispo-bulk-toggle-${f.key}`}
                                    />
                                    <span className="text-[12px] font-bold text-[#111827]">{f.label}</span>
                                </label>
                                <div className="flex-1 min-w-0">
                                    {f.type === "textarea" && (
                                        <textarea
                                            disabled={!on}
                                            value={values[f.key] || ""}
                                            onChange={(e) => setVal(f.key, e.target.value)}
                                            rows={2}
                                            className="w-full text-sm rounded-sm border border-gray-300 px-2 py-1.5 disabled:bg-gray-100 disabled:text-gray-400 focus:border-[#111827] focus:outline-none"
                                            data-testid={`dispo-bulk-input-${f.key}`}
                                        />
                                    )}
                                    {(f.type === "text" || f.type === "number") && (
                                        <Input
                                            type={f.type === "number" ? "number" : "text"}
                                            disabled={!on}
                                            value={values[f.key] || ""}
                                            onChange={(e) => setVal(f.key, e.target.value)}
                                            className="h-9 text-sm rounded-sm disabled:bg-gray-100 disabled:text-gray-400"
                                            data-testid={`dispo-bulk-input-${f.key}`}
                                        />
                                    )}
                                    {f.type === "select" && (
                                        <select
                                            disabled={!on}
                                            value={values[f.key] || ""}
                                            onChange={(e) => setVal(f.key, e.target.value)}
                                            className="w-full h-9 text-sm rounded-sm border border-gray-300 px-2 disabled:bg-gray-100 disabled:text-gray-400 focus:border-[#111827] focus:outline-none"
                                            data-testid={`dispo-bulk-input-${f.key}`}
                                        >
                                            <option value="">— bitte wählen —</option>
                                            {f.options.map((o) => (
                                                <option key={o} value={o}>{o}</option>
                                            ))}
                                        </select>
                                    )}
                                    {f.type === "site" && (
                                        <select
                                            disabled={!on}
                                            value={values[f.key] || ""}
                                            onChange={(e) => setVal(f.key, e.target.value)}
                                            className="w-full h-9 text-sm rounded-sm border border-gray-300 px-2 disabled:bg-gray-100 disabled:text-gray-400 focus:border-[#111827] focus:outline-none"
                                            data-testid={`dispo-bulk-input-${f.key}`}
                                        >
                                            <option value="">— Standort wählen —</option>
                                            {sites.map((s) => (
                                                <option key={s.id} value={s.code}>{`${s.code} · ${s.name}`}</option>
                                            ))}
                                        </select>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className="flex items-center justify-between gap-3 px-5 py-4 border-t border-gray-200 bg-gray-50 rounded-b-sm">
                    <span className="text-[11px] text-gray-500" data-testid="dispo-bulk-active-count">
                        {activeKeys.length} Feld{activeKeys.length === 1 ? "" : "er"} ausgewählt
                    </span>
                    <div className="flex items-center gap-2">
                        <Button type="button" variant="outline" onClick={onClose} className="h-9 rounded-sm text-sm" data-testid="dispo-bulk-cancel">
                            Abbrechen
                        </Button>
                        <Button
                            type="button"
                            onClick={submit}
                            disabled={saving || activeKeys.length === 0}
                            className="h-9 rounded-sm bg-[#111827] text-[#FFCC00] hover:bg-black text-sm font-bold"
                            data-testid="dispo-bulk-submit"
                        >
                            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null}
                            {count} Fahrzeug{count === 1 ? "" : "e"} aktualisieren
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}
